import * as T from 'three';

/** Six damped rigid followers, solved in a planet/vehicle frame. The steady
 * low-speed lag is ~86/101/112 ms down each three-piece chain. Tethers bound
 * fast-flight displacement so a short lag never becomes metres of separation. */
export class CrystalWingDynamics {
  readonly positions:T.Vector3[];
  readonly rotations:T.Quaternion[];
  readonly velocities:T.Vector3[];
  private readonly angularVelocities:T.Vector3[];
  private readonly previous:T.Vector3[];
  private readonly previousRotations:T.Quaternion[];
  private readonly goals:T.Vector3[];
  constructor(private readonly count=6,private readonly linked=true){
    const vectors=()=>Array.from({length:count},()=>new T.Vector3());
    const rotations=()=>Array.from({length:count},()=>new T.Quaternion());
    this.positions=vectors();this.rotations=rotations();this.velocities=vectors();
    this.angularVelocities=vectors();this.previous=vectors();this.previousRotations=rotations();this.goals=vectors();
  }
  private readonly delta=new T.Vector3();
  private readonly tetherOffset=new T.Vector3();
  private readonly force=new T.Vector3();
  private readonly axis=new T.Vector3();
  private readonly goalRotation=new T.Quaternion();
  private readonly error=new T.Quaternion();
  private readonly increment=new T.Quaternion();
  private seeded=false;

  reset():void {this.seeded=false;}

  update(dt:number,targets:readonly T.Vector3[],rotations:readonly T.Quaternion[],scale=1,spread=1,trailing?:T.Vector3):void {
    const jump=this.seeded&&targets.some((p,i)=>p.distanceToSquared(this.previous[i])>9);
    if(!this.seeded||jump||dt>.2){
      for(let i=0;i<this.count;i++){
        this.positions[i].copy(targets[i]);this.previous[i].copy(targets[i]);
        this.rotations[i].copy(rotations[i]);this.previousRotations[i].copy(rotations[i]);
        this.velocities[i].set(0,0,0);this.angularVelocities[i].set(0,0,0);
      }
      this.seeded=true;return;
    }
    if(!Number.isFinite(dt)||dt<=0)return;
    const elapsed=Math.min(dt,.1),steps=Math.ceil(elapsed*240),h=elapsed/steps;
    for(let step=1;step<=steps;step++){
      const alpha=step/steps;
      for(let i=0;i<this.count;i++)this.goals[i].lerpVectors(this.previous[i],targets[i],alpha);
      for(let i=0;i<this.count;i++){
        const rank=this.linked?i%3:Math.floor(i/2),omega=20-rank*1.5,damping=1.8*omega;
        const p=this.positions[i],v=this.velocities[i],av=this.angularVelocities[i],q=this.rotations[i],goal=this.goals[i];
        this.force.copy(goal).sub(p);
        // Only secondary deflection propagates. Formation distances themselves
        // stay authored, so the three modules never collapse into one joint.
        if(this.linked&&rank>0)this.force.addScaledVector(this.delta.copy(this.positions[i-1]).sub(this.goals[i-1]),.10);
        this.force.multiplyScalar(omega*omega).addScaledVector(v,-damping);
        v.addScaledVector(this.force,h);p.addScaledVector(v,h);
        const limit=(.035+.035*spread+rank*.005)*scale;
        this.delta.copy(p).sub(goal);
        // Running extends the tether only behind the player. Keep the original
        // small sideways/vertical allowance so loose following cannot spread
        // the fan into the arms or let it bob independently of the shoulders.
        this.tetherOffset.set(0,0,0);
        if(trailing&&trailing.lengthSq()>1e-10){
          const along=T.MathUtils.clamp(this.delta.dot(trailing)/trailing.lengthSq(),0,.9+rank*.1);
          this.tetherOffset.copy(trailing).multiplyScalar(along);
          this.delta.sub(this.tetherOffset);
        }
        if(this.delta.lengthSq()>limit*limit){
          this.delta.setLength(limit);p.copy(goal).add(this.tetherOffset).add(this.delta);
          const away=v.dot(this.delta)/this.delta.lengthSq();if(away>0)v.addScaledVector(this.delta,-away);
        }

        this.goalRotation.slerpQuaternions(this.previousRotations[i],rotations[i],alpha);
        this.error.copy(q).invert().premultiply(this.goalRotation).normalize();
        if(this.error.w<0)this.error.set(-this.error.x,-this.error.y,-this.error.z,-this.error.w);
        const sine=Math.hypot(this.error.x,this.error.y,this.error.z),angle=2*Math.atan2(sine,this.error.w);
        this.axis.set(this.error.x,this.error.y,this.error.z).multiplyScalar(sine>1e-8?angle/sine:2);
        av.addScaledVector(this.axis,omega*omega*h).multiplyScalar(Math.exp(-damping*h));
        const speed=av.length();
        if(speed>1e-8){this.increment.setFromAxisAngle(this.axis.copy(av).divideScalar(speed),speed*h);q.premultiply(this.increment).normalize();}
        const limitAngle=.05+.075*spread+rank*.005,remaining=q.angleTo(this.goalRotation);
        if(remaining>limitAngle){q.slerp(this.goalRotation,1-limitAngle/remaining);av.multiplyScalar(.8);}
      }
    }
    for(let i=0;i<this.count;i++){this.previous[i].copy(targets[i]);this.previousRotations[i].copy(rotations[i]);}
  }
}

export const CRYSTAL_SPINE_Y=[.125,-.035,-.165,-.275] as const;
export const CRYSTAL_SPINE_WIDTH=[1,.72,.61,.49] as const;
export const CRYSTAL_SPINE_HEIGHT=[1,.74,.62,.50] as const;
export const CRYSTAL_SPINE_PATCH_X=.075;
export const CRYSTAL_SPINE_PATCH_Y=.10;
type SurfaceSample={mesh:T.Mesh;indices:number[];bary:T.Vector3;point:T.Vector3};
export interface CrystalSpineSegment {
  position:T.Vector3;
  rotation:T.Quaternion;
  surface:T.Vector3;
  attached:boolean;
  samples:SurfaceSample[];
  patch:T.Vector3[];
}

/** Fit the anatomical centreline in bind space, independent of the pose at
 * load time. Cached skin patches then carry each vertebra through live bend
 * and twist. There is no shared rigid plate and no spring lag. */
export class CrystalWingDockAnchor {
  readonly segments:CrystalSpineSegment[]=CRYSTAL_SPINE_Y.map(()=>({position:new T.Vector3(),rotation:new T.Quaternion(),surface:new T.Vector3(),attached:false,samples:[],patch:Array.from({length:4},()=>new T.Vector3())}));
  attached=false;
  private readonly a=new T.Vector3();
  private readonly b=new T.Vector3();
  private readonly c=new T.Vector3();
  private readonly back=new T.Vector3();
  private readonly up=new T.Vector3();
  private readonly right=new T.Vector3();
  private readonly referenceBack=new T.Vector3();
  private readonly matrix=new T.Matrix4();
  private readonly inverse=new T.Quaternion();
  private readonly ray=new T.Raycaster();

  reset():void {for(const s of this.segments){s.samples.length=0;s.attached=false;}this.attached=false;}
  bind(meshes:readonly T.Mesh[],center:T.Vector3,frame:T.Quaternion,scale:number,bones?:ReadonlyMap<string,T.Bone>,skeleton?:T.Skeleton):void {
    this.reset();
    this.back.set(0,0,1).applyQuaternion(frame);this.up.set(0,1,0).applyQuaternion(frame);this.right.set(1,0,0).applyQuaternion(frame);
    // A posed hips/head line is not the spine: sprint twist can put its rays
    // several centimetres to one side, permanently attaching there. Recover
    // the real pelvis-to-chest chain from the inverse binds instead.
    const chain:T.Vector3[]=[];
    for(const name of ['Hips','spine_01','spine_02','spine_03','spine_04','Spine2']){
      const bone=bones?.get(name),index=bone&&skeleton?skeleton.bones.indexOf(bone):-1;
      if(index>=0)chain.push(new T.Vector3().setFromMatrixPosition(this.matrix.copy(skeleton!.boneInverses[index]).invert()));
    }
    const anatomical=chain.length===6;
    const distances=chain.map((p,i)=>i?p.distanceTo(chain[i-1]):0);
    for(let i=1;i<distances.length;i++)distances[i]+=distances[i-1];
    const span=anatomical?distances[distances.length-1]:.4*scale;
    const sampleCenter=new T.Vector3();
    const candidates:T.Mesh[]=[],originals=new Map<T.Mesh,T.Mesh>();
    if(anatomical){
      this.up.subVectors(chain[chain.length-1],chain[0]).normalize();
      this.right.set(1,0,0);this.back.crossVectors(this.right,this.up).normalize();
      this.up.crossVectors(this.back,this.right).normalize();
      // Morphs include the chosen body shape. Bypass only the animation skin
      // transform, without resetting the live skeleton or changing its pose.
      for(const mesh of meshes){
        if(!mesh.visible)continue;
        const position=mesh.geometry.attributes.position,vertices=new Float32Array(position.count*3);
        for(let i=0;i<position.count;i++){
          T.Mesh.prototype.getVertexPosition.call(mesh,i,this.a);
          if(mesh instanceof T.SkinnedMesh)this.a.applyMatrix4(mesh.bindMatrix);
          else this.a.applyMatrix4(mesh.matrixWorld);
          this.a.toArray(vertices,i*3);
        }
        const geometry=new T.BufferGeometry().setAttribute('position',new T.BufferAttribute(vertices,3));
        if(mesh.geometry.index)geometry.setIndex(mesh.geometry.index);
        const proxy=new T.Mesh(geometry,mesh.material);candidates.push(proxy);originals.set(proxy,mesh);
      }
    }else{
      candidates.push(...meshes.filter(mesh=>mesh.visible));
      for(const mesh of candidates)if(mesh instanceof T.SkinnedMesh){mesh.computeBoundingSphere();if(mesh.boundingBox)mesh.computeBoundingBox();}
    }
    this.ray.ray.direction.copy(this.back).negate();this.ray.near=0;this.ray.far=scale;
    for(let i=0;i<this.segments.length;i++){
      const segment=this.segments[i],width=CRYSTAL_SPINE_PATCH_X*CRYSTAL_SPINE_WIDTH[i],height=CRYSTAL_SPINE_PATCH_Y*CRYSTAL_SPINE_HEIGHT[i]*(anatomical?span/(.4*scale):1);
      if(anatomical){
        const distance=(CRYSTAL_SPINE_Y[i]-CRYSTAL_SPINE_Y[3])/.4*span;
        let j=1;while(j<chain.length-1&&distances[j]<distance)j++;
        sampleCenter.lerpVectors(chain[j-1],chain[j],(distance-distances[j-1])/(distances[j]-distances[j-1]));
      }else sampleCenter.copy(center).addScaledVector(this.up,CRYSTAL_SPINE_Y[i]*scale);
      for(const [x,y]of [[0,0],[-width,0],[width,0],[0,height],[0,-height]]){
        this.ray.ray.origin.copy(sampleCenter).addScaledVector(this.right,x*scale).addScaledVector(this.up,y*scale).addScaledVector(this.back,.8*scale);
        const hits:T.Intersection[]=[];
        for(const mesh of candidates)mesh.raycast(this.ray,hits);
        hits.sort((a,b)=>a.distance-b.distance);
        const hit=hits.find(h=>h.face&&h.distance<.82*scale);
        if(!hit?.face)break;
        const fitted=hit.object as T.Mesh;
        const sample:SurfaceSample={mesh:originals.get(fitted)??fitted,indices:[hit.face.a,hit.face.b,hit.face.c],bary:new T.Vector3(),point:new T.Vector3()};
        fitted.getVertexPosition(hit.face.a,this.a).applyMatrix4(fitted.matrixWorld);
        fitted.getVertexPosition(hit.face.b,this.b).applyMatrix4(fitted.matrixWorld);
        fitted.getVertexPosition(hit.face.c,this.c).applyMatrix4(fitted.matrixWorld);
        T.Triangle.getBarycoord(hit.point,this.a,this.b,this.c,sample.bary);segment.samples.push(sample);
      }
      segment.attached=segment.samples.length===5;
    }
    for(const proxy of originals.keys())proxy.geometry.dispose();
    this.attached=this.segments.every(s=>s.attached);this.update(frame,scale);
  }
  private vertices(sample:SurfaceSample):void {
    const {mesh,indices}=sample;
    mesh.getVertexPosition(indices[0],this.a).applyMatrix4(mesh.matrixWorld);
    mesh.getVertexPosition(indices[1],this.b).applyMatrix4(mesh.matrixWorld);
    mesh.getVertexPosition(indices[2],this.c).applyMatrix4(mesh.matrixWorld);
  }
  update(frame:T.Quaternion,scale:number):void {
    this.referenceBack.set(0,0,1).applyQuaternion(frame);
    for(let i=0;i<this.segments.length;i++){
      const segment=this.segments[i];
      if(!segment.attached)continue;
      const samples=segment.samples;
      for(const sample of samples){
        this.vertices(sample);
        sample.point.copy(this.a).multiplyScalar(sample.bary.x).addScaledVector(this.b,sample.bary.y).addScaledVector(this.c,sample.bary.z);
      }
      segment.surface.copy(samples[0].point);
      this.right.subVectors(samples[2].point,samples[1].point);
      this.up.subVectors(samples[3].point,samples[4].point).normalize();
      this.back.crossVectors(this.right,this.up);
      if(this.back.lengthSq()<1e-10){this.back.copy(this.referenceBack);this.up.set(0,1,0).applyQuaternion(frame);}
      this.back.normalize();if(this.back.dot(this.referenceBack)<0)this.back.negate();
      this.right.crossVectors(this.up,this.back).normalize();this.up.crossVectors(this.back,this.right).normalize();
      segment.rotation.setFromRotationMatrix(this.matrix.makeBasis(this.right,this.up,this.back));
      // The new mesh's underside is z=0. Seat it 0.6 mm into the skin/cloth;
      // the shallow gem and fitted chevrons articulate with this local patch.
      segment.position.copy(segment.surface).addScaledVector(this.back,-.0006*scale);
      this.inverse.copy(segment.rotation).invert();
      for(let edge=0;edge<4;edge++){
        // Preserve the whole surface patch, including its bend and shear.
        // Short plates articulate; their thin faces also conform between the
        // five anchors, so a seated centre cannot leave lifted edges.
        const p=segment.patch[edge];p.subVectors(samples[edge+1].point,segment.surface).applyQuaternion(this.inverse).divideScalar(scale);
        p.x/=CRYSTAL_SPINE_WIDTH[i];p.y/=CRYSTAL_SPINE_HEIGHT[i];
      }
    }
  }
}
